import { getDb, persistDb } from './client';
import type {
  AppSession,
  FrictionSubtype,
  IdleSegment,
  InterruptionSource,
  ParkingLotEntry,
  Ping,
  PingType,
  RescueKind,
  RescueOutcome,
  RescueScript,
  RescueSession
} from '../types';

type SqlValue = string | number | null;
type SqlExecResult = Array<{ columns: string[]; values: SqlValue[][] }>;

function asInt(value: SqlValue, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : fallback;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? Math.trunc(n) : fallback;
  }
  return fallback;
}

function asEpochMs(value: SqlValue, fallback = 0): number {
  const n = asInt(value, fallback);
  // Backward-compat: older/local data may store epoch in seconds.
  if (n > 0 && n < 100_000_000_000) return n * 1000;
  return n;
}

function rows<T>(result: SqlExecResult): T[] {
  if (!result[0]) return [];
  const first = result[0] as unknown as { columns?: string[]; lc?: string[]; values?: SqlValue[][] };
  const columns = first.columns ?? first.lc ?? [];
  const values = first.values ?? [];
  return values.map((v: SqlValue[]) => {
    const out: Record<string, SqlValue> = {};
    columns.forEach((col: string, i: number) => {
      out[col] = v[i];
    });
    return out as T;
  });
}

function queryRows<T>(db: any, sql: string, params: SqlValue[] = []): T[] {
  const stmt = db.prepare(sql);
  stmt.bind(params);
  const out: T[] = [];
  while (stmt.step()) {
    out.push(stmt.getAsObject() as T);
  }
  stmt.free();
  return out;
}

function runWithParams(db: any, sql: string, params: SqlValue[] = []): void {
  const stmt = db.prepare(sql);
  stmt.run(params);
  stmt.free();
}

function normalizePing(row: Ping): Ping {
  const rawFrictionStart = row.friction_started_ms as unknown as SqlValue;
  return {
    ...row,
    ts_ms: asEpochMs(row.ts_ms as unknown as SqlValue, Date.now()),
    friction_started_ms: rawFrictionStart === null ? null : asEpochMs(rawFrictionStart, Date.now()),
    created_at_ms: asEpochMs(row.created_at_ms as unknown as SqlValue, Date.now()),
    subtype: (row.subtype ?? null) as FrictionSubtype | null,
    interruption_source: (row.interruption_source ?? null) as InterruptionSource | null,
    tz_offset_min: asInt(row.tz_offset_min as unknown as SqlValue, 0)
  };
}

function normalizeRescue(row: RescueSession): RescueSession {
  const rawEnd = row.ts_end_ms as unknown as SqlValue;
  return {
    ...row,
    ts_start_ms: asEpochMs(row.ts_start_ms as unknown as SqlValue, Date.now()),
    ts_end_ms: rawEnd === null ? null : asEpochMs(rawEnd, Date.now()),
    duration_s: asInt(row.duration_s as unknown as SqlValue, 0),
    completed: asInt(row.completed as unknown as SqlValue, 0) === 1 ? 1 : 0,
    tiny_win: row.tiny_win ?? null,
    outcome:
      row.outcome === 'helped' || row.outcome === 'a_bit' || row.outcome === 'not_really'
        ? row.outcome
        : null,
    prep_phone_silenced:
      row.prep_phone_silenced === null ? null : asInt(row.prep_phone_silenced as unknown as SqlValue, 0) === 1 ? 1 : 0,
    prep_tabs_closed: row.prep_tabs_closed === null ? null : asInt(row.prep_tabs_closed as unknown as SqlValue, 0) === 1 ? 1 : 0,
    prep_doc_open: row.prep_doc_open === null ? null : asInt(row.prep_doc_open as unknown as SqlValue, 0) === 1 ? 1 : 0,
    prep_timer_visible:
      row.prep_timer_visible === null ? null : asInt(row.prep_timer_visible as unknown as SqlValue, 0) === 1 ? 1 : 0,
    tz_offset_min: asInt(row.tz_offset_min as unknown as SqlValue, 0)
  };
}

function normalizeParkingLot(row: ParkingLotEntry): ParkingLotEntry {
  return {
    ...row,
    ts_ms: asEpochMs(row.ts_ms as unknown as SqlValue, Date.now()),
    text: row.text ?? '',
    ping_id: row.ping_id ?? null
  };
}

export async function createPing(p: Ping): Promise<void> {
  const db = await getDb();
  const ts = Number.isFinite(p.ts_ms) ? Math.trunc(p.ts_ms) : Date.now();
  const createdAt = Number.isFinite(p.created_at_ms) ? Math.trunc(p.created_at_ms) : ts;
  const tz = Number.isFinite(p.tz_offset_min) ? Math.trunc(p.tz_offset_min) : -new Date(ts).getTimezoneOffset();
  const type = p.type ?? 'cant_start';
  const id = p.id ?? `ping_${ts}`;

  const stmt = db.prepare(
    `INSERT INTO ping(id, ts_ms, friction_started_ms, type, subtype, note, interruption_source, tz_offset_min, created_at_ms)
     VALUES ($id, $ts, $frictionStart, $type, $subtype, $note, $interruption, $tz, $createdAt)`
  );
  stmt.run({
    $id: id,
    $ts: ts,
    $frictionStart: p.friction_started_ms === null ? null : Math.trunc(p.friction_started_ms),
    $type: type,
    $subtype: p.subtype ?? null,
    $note: p.note ?? null,
    $interruption: p.interruption_source ?? null,
    $tz: tz,
    $createdAt: createdAt
  });
  stmt.free();
  await persistDb();
}

export async function updatePingNote(id: string, note: string | null): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE ping SET note = ? WHERE id = ?', [note, id]);
  await persistDb();
}

export async function updatePingInterruption(id: string, source: InterruptionSource | null): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE ping SET interruption_source = ? WHERE id = ?', [source, id]);
  await persistDb();
}

export async function updatePingSubtype(id: string, subtype: FrictionSubtype | null): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE ping SET subtype = ? WHERE id = ?', [subtype, id]);
  await persistDb();
}

export async function deletePing(id: string): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'DELETE FROM ping WHERE id = ?', [id]);
  await persistDb();
}

export async function canUndoPing(id: string): Promise<boolean> {
  const db = await getDb();
  const result = queryRows<{ c: number }>(db, 'SELECT COUNT(*) as c FROM rescue_session WHERE ping_id = ?', [id]);
  return Number(result[0]?.c ?? 0) === 0;
}

export async function listPings(startMs = 0, endMs = Date.now(), types?: PingType[]): Promise<Ping[]> {
  const db = await getDb();
  // Backward-compat: include rows where timestamps were historically persisted in seconds.
  let sql =
    'SELECT * FROM ping WHERE (CASE WHEN ts_ms > 0 AND ts_ms < 100000000000 THEN ts_ms * 1000 ELSE ts_ms END) BETWEEN ? AND ?';
  const params: SqlValue[] = [startMs, endMs];
  if (types && types.length > 0) {
    sql += ` AND type IN (${types.map(() => '?').join(',')})`;
    params.push(...types);
  }
  sql += ' ORDER BY (CASE WHEN ts_ms > 0 AND ts_ms < 100000000000 THEN ts_ms * 1000 ELSE ts_ms END) DESC';
  return queryRows<Ping>(db, sql, params).map(normalizePing);
}

export async function getPingById(id: string): Promise<Ping | null> {
  const db = await getDb();
  const result = queryRows<Ping>(db, 'SELECT * FROM ping WHERE id = ? LIMIT 1', [id]).map(normalizePing);
  return result[0] ?? null;
}

export async function createRescue(r: RescueSession): Promise<void> {
  const db = await getDb();
  const tsStart = Number.isFinite(r.ts_start_ms) ? Math.trunc(r.ts_start_ms) : Date.now();
  const duration = Number.isFinite(r.duration_s) ? Math.max(0, Math.trunc(r.duration_s)) : 0;
  const tz = Number.isFinite(r.tz_offset_min) ? Math.trunc(r.tz_offset_min) : -new Date(tsStart).getTimezoneOffset();
  const stmt = db.prepare(
    `INSERT INTO rescue_session(
      id, ping_id, ts_start_ms, ts_end_ms, kind, duration_s, completed, note, tiny_win,
      outcome, prep_phone_silenced, prep_tabs_closed, prep_doc_open, prep_timer_visible, tz_offset_min
    )
    VALUES ($id, $pingId, $tsStart, $tsEnd, $kind, $duration, $completed, $note, $tinyWin, $outcome, $prepPhone, $prepTabs, $prepDoc, $prepTimer, $tz)`
  );
  stmt.run({
    $id: r.id ?? `rescue_${tsStart}`,
    $pingId: r.ping_id ?? null,
    $tsStart: tsStart,
    $tsEnd: Number.isFinite(r.ts_end_ms as number) ? Math.trunc(r.ts_end_ms as number) : null,
    $kind: r.kind ?? 'stand_water',
    $duration: duration,
    $completed: r.completed === 1 ? 1 : 0,
    $note: r.note ?? null,
    $tinyWin: r.tiny_win ?? null,
    $outcome: r.outcome ?? null,
    $prepPhone: r.prep_phone_silenced,
    $prepTabs: r.prep_tabs_closed,
    $prepDoc: r.prep_doc_open,
    $prepTimer: r.prep_timer_visible,
    $tz: tz
  });
  stmt.free();
  await persistDb();
}

export async function finishRescue(id: string, completed: 0 | 1, tsEnd: number, note?: string | null): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE rescue_session SET ts_end_ms = ?, completed = ?, note = COALESCE(?, note) WHERE id = ?', [
    tsEnd,
    completed,
    note ?? null,
    id
  ]);
  await persistDb();
}

export async function updateRescueTinyWin(id: string, tinyWin: string | null): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE rescue_session SET tiny_win = ? WHERE id = ?', [tinyWin, id]);
  await persistDb();
}

export async function updateRescueOutcome(id: string, outcome: RescueOutcome | null): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE rescue_session SET outcome = ? WHERE id = ?', [outcome, id]);
  await persistDb();
}

export async function listRescuesByPing(pingId: string): Promise<RescueSession[]> {
  const db = await getDb();
  return queryRows<RescueSession>(db, 'SELECT * FROM rescue_session WHERE ping_id = ? ORDER BY ts_start_ms DESC', [pingId]).map(
    normalizeRescue
  );
}

export async function listRescues(startMs = 0, endMs = Date.now()): Promise<RescueSession[]> {
  const db = await getDb();
  return queryRows<RescueSession>(
    db,
    `SELECT * FROM rescue_session
     WHERE (CASE WHEN ts_start_ms > 0 AND ts_start_ms < 100000000000 THEN ts_start_ms * 1000 ELSE ts_start_ms END) BETWEEN ? AND ?
     ORDER BY (CASE WHEN ts_start_ms > 0 AND ts_start_ms < 100000000000 THEN ts_start_ms * 1000 ELSE ts_start_ms END) DESC`,
    [startMs, endMs]
  ).map(normalizeRescue);
}

export async function createAppSession(s: AppSession): Promise<void> {
  const db = await getDb();
  const tsStart = Number.isFinite(s.ts_start_ms) ? Math.trunc(s.ts_start_ms) : Date.now();
  const stmt = db.prepare(
    'INSERT INTO app_session(id, ts_start_ms, ts_end_ms, platform, app_version) VALUES ($id, $start, $end, $platform, $version)'
  );
  stmt.run({
    $id: s.id ?? `app_${tsStart}`,
    $start: tsStart,
    $end: Number.isFinite(s.ts_end_ms as number) ? Math.trunc(s.ts_end_ms as number) : null,
    $platform: s.platform || 'unknown',
    $version: s.app_version || 'dev'
  });
  stmt.free();
  await persistDb();
}

export async function clearAllUserData(): Promise<void> {
  const db = await getDb();
  db.run('BEGIN');
  try {
    runWithParams(db, 'DELETE FROM rescue_session');
    runWithParams(db, 'DELETE FROM ping');
    runWithParams(db, 'DELETE FROM parking_lot');
    runWithParams(db, 'DELETE FROM rescue_script');
    runWithParams(db, 'DELETE FROM idle_segment');
    runWithParams(db, 'DELETE FROM app_session');
    db.run('COMMIT');
  } catch (error) {
    db.run('ROLLBACK');
    throw error;
  }
  await persistDb();
}

export async function endAppSession(id: string, tsEnd: number): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'UPDATE app_session SET ts_end_ms = ? WHERE id = ?', [tsEnd, id]);
  await persistDb();
}

export async function createIdleSegment(s: IdleSegment): Promise<void> {
  const db = await getDb();
  const start = Number.isFinite(s.ts_start_ms) ? Math.trunc(s.ts_start_ms) : Date.now();
  const end = Number.isFinite(s.ts_end_ms) ? Math.max(start, Math.trunc(s.ts_end_ms)) : start;
  const stmt = db.prepare('INSERT INTO idle_segment(id, ts_start_ms, ts_end_ms, source) VALUES ($id, $start, $end, $source)');
  stmt.run({
    $id: s.id ?? `idle_${start}`,
    $start: start,
    $end: end,
    $source: s.source || 'in_app_no_interaction'
  });
  stmt.free();
  await persistDb();
}

export async function upsertRescueScript(kind: RescueKind, script: string): Promise<void> {
  const db = await getDb();
  runWithParams(db, 'INSERT OR REPLACE INTO rescue_script(kind, script) VALUES (?, ?)', [kind, script]);
  await persistDb();
}

export async function listRescueScripts(): Promise<RescueScript[]> {
  const db = await getDb();
  return queryRows<RescueScript>(db, 'SELECT * FROM rescue_script');
}

export async function createParkingLotEntry(entry: ParkingLotEntry): Promise<void> {
  const db = await getDb();
  const stmt = db.prepare('INSERT INTO parking_lot(id, ts_ms, text, ping_id) VALUES ($id, $ts, $text, $pingId)');
  stmt.run({
    $id: entry.id,
    $ts: Math.trunc(entry.ts_ms),
    $text: entry.text,
    $pingId: entry.ping_id ?? null
  });
  stmt.free();
  await persistDb();
}

export async function listParkingLotEntries(startMs = 0, endMs = Date.now()): Promise<ParkingLotEntry[]> {
  const db = await getDb();
  return queryRows<ParkingLotEntry>(
    db,
    `SELECT * FROM parking_lot
     WHERE (CASE WHEN ts_ms > 0 AND ts_ms < 100000000000 THEN ts_ms * 1000 ELSE ts_ms END) BETWEEN ? AND ?
     ORDER BY (CASE WHEN ts_ms > 0 AND ts_ms < 100000000000 THEN ts_ms * 1000 ELSE ts_ms END) DESC`,
    [startMs, endMs]
  ).map(normalizeParkingLot);
}

export async function exportAllTables(): Promise<Record<string, unknown[]>> {
  const db = await getDb();
  return {
    meta: rows(db.exec('SELECT * FROM meta') as SqlExecResult),
    ping: rows(db.exec('SELECT * FROM ping ORDER BY ts_ms ASC') as SqlExecResult),
    rescue_session: rows(db.exec('SELECT * FROM rescue_session ORDER BY ts_start_ms ASC') as SqlExecResult),
    app_session: rows(db.exec('SELECT * FROM app_session ORDER BY ts_start_ms ASC') as SqlExecResult),
    idle_segment: rows(db.exec('SELECT * FROM idle_segment ORDER BY ts_start_ms ASC') as SqlExecResult),
    rescue_script: rows(db.exec('SELECT * FROM rescue_script ORDER BY kind ASC') as SqlExecResult),
    parking_lot: rows(db.exec('SELECT * FROM parking_lot ORDER BY ts_ms ASC') as SqlExecResult)
  };
}
